import { useCallback, useEffect, useRef, useState } from 'react';
import { PA_JOB_ACTIVE_STATUSES, PaJobSchema } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import type {
  PaArtifact,
  PaJob,
  PaJobDecideRequest,
  PaJobStepStatus,
  PaToolApproval,
  WorkspaceState,
} from '@easyflow/shared';
import type { PendingApproval, RoundEvent, WorkspaceUiArtifact } from './types';

// L'apiClient ritorna `{success, data, ...}` dove `data` è il payload
// definito da T. Il backend espone array direttamente per list e
// snapshot completo per state.

interface StateSnapshotPayload {
  conversationId: string;
  state: WorkspaceState;
  version: number;
  changedAt: string;
  reason: string | null;
}

export function usePaArtifacts(conversationId: string | null) {
  const [artifacts, setArtifacts] = useState<WorkspaceUiArtifact[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setArtifacts([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get<PaArtifact[]>(
        `/personal-assistant/workspace/conversations/${conversationId}/artifacts`,
      );
      setArtifacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('[usePaArtifacts] load failed', err);
      setArtifacts([]);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { refresh(); }, [refresh]);

  const upsert = useCallback((incoming: PaArtifact) => {
    setArtifacts(prev => {
      const idx = prev.findIndex(a => a.slug === incoming.slug);
      if (idx === -1) return [...prev, { ...incoming, isFresh: true }];
      const next = [...prev];
      next[idx] = { ...incoming, isFresh: true };
      return next;
    });
  }, []);

  const remove = useCallback(async (id: string) => {
    await apiClient.delete(`/personal-assistant/workspace/artifacts/${id}`);
    setArtifacts(prev => prev.filter(a => a.id !== id));
  }, []);

  return { artifacts, loading, refresh, upsert, remove };
}

/**
 * Vista globale degli artefatti dell'utente (cross-conversazione) per il
 * pannello Artefatti del workspace. Dedup per `id` (slug NON è univoco tra
 * conversazioni). La cancellazione lato API è gestita da `usePaArtifacts`:
 * qui `remove` è solo locale per tenere allineata la lista.
 */
export function usePaAllArtifacts() {
  const [artifacts, setArtifacts] = useState<WorkspaceUiArtifact[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<PaArtifact[]>(
        `/personal-assistant/workspace/artifacts`,
      );
      setArtifacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('[usePaAllArtifacts] load failed', err);
      setArtifacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const upsert = useCallback((incoming: PaArtifact) => {
    setArtifacts(prev => {
      const idx = prev.findIndex(a => a.id === incoming.id);
      if (idx === -1) return [{ ...incoming, isFresh: true }, ...prev];
      const next = [...prev];
      next[idx] = { ...incoming, isFresh: true };
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setArtifacts(prev => prev.filter(a => a.id !== id));
  }, []);

  return { artifacts, loading, refresh, upsert, remove };
}

export function usePaApprovals(conversationId: string | null) {
  const [pending, setPending] = useState<PendingApproval[]>([]);

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setPending([]);
      return;
    }
    try {
      const res = await apiClient.get<PaToolApproval[]>(
        `/personal-assistant/workspace/conversations/${conversationId}/approvals?status=pending`,
      );
      const items = Array.isArray(res.data) ? res.data : [];
      const now = new Date().toISOString();
      setPending(items.map(a => ({ ...a, receivedAt: now })));
    } catch (err) {
      console.warn('[usePaApprovals] load failed', err);
    }
  }, [conversationId]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback((approval: PaToolApproval) => {
    setPending(prev => {
      if (prev.some(p => p.id === approval.id)) return prev;
      return [...prev, { ...approval, receivedAt: new Date().toISOString() }];
    });
  }, []);

  const remove = useCallback((id: string) => {
    setPending(prev => prev.filter(p => p.id !== id));
  }, []);

  const decide = useCallback(async (
    id: string,
    decision: 'approved' | 'rejected',
    notes?: string,
    overrideArgs?: Record<string, unknown>,
  ) => {
    const body: { decision: 'approved' | 'rejected'; note?: string; overrideArgs?: Record<string, unknown> } = { decision };
    if (notes) body.note = notes;
    if (overrideArgs) body.overrideArgs = overrideArgs;
    await apiClient.post(`/personal-assistant/workspace/approvals/${id}/decide`, body);
    remove(id);
  }, [remove]);

  return { pending, refresh, add, remove, decide };
}

function newerJob(first: PaJob, second: PaJob): PaJob {
  if (first.statusVersion !== second.statusVersion) {
    return first.statusVersion > second.statusVersion ? first : second;
  }
  return Date.parse(first.updatedAt) >= Date.parse(second.updatedAt) ? first : second;
}

function newestFirst(jobs: PaJob[]): PaJob[] {
  return [...jobs].sort((first, second) => Date.parse(second.createdAt) - Date.parse(first.createdAt));
}

interface PaJobsScope {
  assistantId: string;
  conversationId: string | null;
  enabled: boolean;
}

function sameJobsScope(first: PaJobsScope, second: PaJobsScope): boolean {
  return first.assistantId === second.assistantId
    && first.conversationId === second.conversationId
    && first.enabled === second.enabled;
}

export function usePaJobs(assistantId: string, conversationId: string | null, enabled = true) {
  const [conversationJobs, setConversationJobs] = useState<PaJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopeRef = useRef({ assistantId, conversationId, enabled });
  const jobsRef = useRef<PaJob[]>([]);
  const requestRef = useRef(0);
  const updateRevisionRef = useRef(0);
  const jobRevisionRef = useRef(new Map<string, number>());
  const actionRef = useRef(new Map<string, Promise<void>>());

  scopeRef.current = { assistantId, conversationId, enabled };
  jobsRef.current = conversationJobs;

  const refresh = useCallback(async () => {
    const expectedScope: PaJobsScope = { assistantId, conversationId, enabled };
    if (!sameJobsScope(scopeRef.current, expectedScope)) return;
    const request = ++requestRef.current;
    const updateRevision = updateRevisionRef.current;
    if (!conversationId || !enabled) {
      setConversationJobs([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        assistant_id: assistantId,
        conversation_id: conversationId,
        limit: '100',
      });
      const res = await apiClient.get<unknown>(`/personal-assistant/workspace/jobs?${query.toString()}`);
      if (
        request !== requestRef.current
        || scopeRef.current.assistantId !== assistantId
        || scopeRef.current.conversationId !== conversationId
        || !scopeRef.current.enabled
      ) return;
      const parsed = PaJobSchema.array().safeParse(res.data);
      if (!parsed.success) throw new Error('Risposta piani non valida');
      const loaded = parsed.data.filter(
        job => job.assistantId === assistantId && job.conversationId === conversationId,
      );
      setConversationJobs(current => {
        if (updateRevisionRef.current === updateRevision) return newestFirst(loaded);
        const merged = new Map(loaded.map(job => [job.id, job]));
        for (const job of current) {
          const persisted = merged.get(job.id);
          const changedDuringRequest = (jobRevisionRef.current.get(job.id) ?? 0) > updateRevision;
          merged.set(job.id, persisted && !changedDuringRequest ? newerJob(job, persisted) : job);
        }
        return newestFirst([...merged.values()]);
      });
    } catch (caught) {
      if (
        request !== requestRef.current
        || scopeRef.current.assistantId !== assistantId
        || scopeRef.current.conversationId !== conversationId
        || !scopeRef.current.enabled
      ) return;
      const message = caught instanceof Error ? caught.message : 'Impossibile caricare i piani della conversazione';
      setError(message);
    } finally {
      if (
        request === requestRef.current
        && scopeRef.current.assistantId === assistantId
        && scopeRef.current.conversationId === conversationId
        && scopeRef.current.enabled
      ) setLoading(false);
    }
  }, [assistantId, conversationId, enabled]);

  useEffect(() => {
    requestRef.current += 1;
    setConversationJobs([]);
    setError(null);
    void refresh();
  }, [refresh]);

  const hasActiveJobs = conversationJobs.some(job => PA_JOB_ACTIVE_STATUSES.includes(job.status));

  useEffect(() => {
    if (!enabled || !conversationId || !hasActiveJobs) return;
    let cancelled = false;
    let timer: number | null = null;
    const poll = (): void => {
      void refresh().finally(() => {
        if (!cancelled) timer = window.setTimeout(poll, 3000);
      });
    };
    timer = window.setTimeout(poll, 3000);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [conversationId, enabled, hasActiveJobs, refresh]);

  const upsert = useCallback((job: PaJob) => {
    const scope = scopeRef.current;
    if (job.assistantId !== scope.assistantId || job.conversationId !== scope.conversationId) return;
    updateRevisionRef.current += 1;
    jobRevisionRef.current.set(job.id, updateRevisionRef.current);
    setConversationJobs(previous => {
      const current = previous.find(item => item.id === job.id);
      const incoming = current ? newerJob(job, current) : job;
      return newestFirst([incoming, ...previous.filter(item => item.id !== job.id)]);
    });
  }, []);

  const applyStep = useCallback((jobId: string, stepId: string, status: PaJobStepStatus) => {
    updateRevisionRef.current += 1;
    jobRevisionRef.current.set(jobId, updateRevisionRef.current);
    setConversationJobs(previous => previous.map(job => job.id !== jobId
      ? job
      : { ...job, plan: job.plan.map(step => step.id === stepId ? { ...step, status } : step) }));
  }, []);

  const runAction = useCallback((id: string, operation: () => Promise<void>): Promise<void> => {
    const existing = actionRef.current.get(id);
    if (existing) return existing;
    const promise = operation().finally(() => {
      if (actionRef.current.get(id) === promise) actionRef.current.delete(id);
    });
    actionRef.current.set(id, promise);
    return promise;
  }, []);

  const decide = useCallback((
    id: string,
    decision: 'approved' | 'rejected',
    note: string | undefined,
    expectedVersion: number,
  ): Promise<void> => {
    const actionScope = { ...scopeRef.current };
    return runAction(id, async () => {
      if (!jobsRef.current.some(job => job.id === id && job.status === 'proposed')) {
        throw new Error('Il piano non è più in attesa di approvazione');
      }
      const body: PaJobDecideRequest = { decision, expectedVersion };
      if (note && note.trim()) body.note = note.trim();
      try {
        const res = await apiClient.post<unknown>(`/personal-assistant/workspace/jobs/${id}/decide`, body);
        const parsed = PaJobSchema.safeParse(res.data);
        if (!parsed.success) throw new Error('Risposta decisione piano non valida');
        upsert(parsed.data);
      } catch (caught) {
        if (sameJobsScope(scopeRef.current, actionScope)) {
          const message = caught instanceof Error ? caught.message : 'Impossibile aggiornare il piano';
          setError(message);
          await refresh();
        }
        throw caught;
      }
    });
  }, [refresh, runAction, upsert]);

  const cancel = useCallback((id: string, reason?: string): Promise<void> => {
    const actionScope = { ...scopeRef.current };
    return runAction(id, async () => {
      if (!jobsRef.current.some(job => job.id === id && PA_JOB_ACTIVE_STATUSES.includes(job.status))) {
        throw new Error('Il piano non è più annullabile');
      }
      try {
        const res = await apiClient.post<unknown>(
          `/personal-assistant/workspace/jobs/${id}/cancel`,
          reason && reason.trim() ? { reason: reason.trim() } : {},
        );
        const parsed = PaJobSchema.safeParse(res.data);
        if (!parsed.success) throw new Error('Risposta annullamento piano non valida');
        upsert(parsed.data);
      } catch (caught) {
        if (sameJobsScope(scopeRef.current, actionScope)) {
          const message = caught instanceof Error ? caught.message : 'Impossibile annullare il piano';
          setError(message);
          await refresh();
        }
        throw caught;
      }
    });
  }, [refresh, runAction, upsert]);

  return { conversationJobs, loading, error, refresh, upsert, applyStep, decide, cancel };
}

export function usePaWorkspaceState(conversationId: string | null) {
  const [state, setState] = useState<WorkspaceState>('idle');
  const [version, setVersion] = useState<number>(0);
  const versionRef = useRef(0);

  useEffect(() => { versionRef.current = version; }, [version]);

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setState('idle');
      setVersion(0);
      return;
    }
    try {
      const res = await apiClient.get<StateSnapshotPayload>(
        `/personal-assistant/workspace/conversations/${conversationId}/state`,
      );
      const data = res.data;
      if (data) {
        setState(data.state);
        setVersion(data.version);
      }
    } catch (err) {
      console.warn('[usePaWorkspaceState] load failed', err);
    }
  }, [conversationId]);

  useEffect(() => { refresh(); }, [refresh]);

  const setLocal = useCallback((next: WorkspaceState, nextVersion?: number) => {
    setState(next);
    if (typeof nextVersion === 'number') setVersion(nextVersion);
  }, []);

  const interrupt = useCallback(async () => {
    if (!conversationId) return;
    try {
      await apiClient.post(`/personal-assistant/workspace/conversations/${conversationId}/interrupt`, {
        expectedVersion: versionRef.current,
      });
      setLocal('paused');
    } catch (err) {
      console.warn('[usePaWorkspaceState] interrupt failed', err);
    }
  }, [conversationId, setLocal]);

  const resume = useCallback(async (instruction?: string) => {
    if (!conversationId) return;
    try {
      const body: { expectedVersion: number; instruction?: string } = {
        expectedVersion: versionRef.current,
      };
      if (instruction && instruction.trim()) body.instruction = instruction.trim();
      await apiClient.post(`/personal-assistant/workspace/conversations/${conversationId}/resume`, body);
      setLocal('idle');
      await refresh();
    } catch (err) {
      console.warn('[usePaWorkspaceState] resume failed', err);
    }
  }, [conversationId, setLocal, refresh]);

  return { state, version, setLocal, refresh, interrupt, resume };
}
